import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, Plus, Trash2, X } from 'lucide-react';

/* Powerplay-style Work Breakdown Structure tree.
   - Unlimited nesting (task -> subtask -> ...)
   - Inline rename, work category, target qty + unit
   - Add subtask, delete, Log Work Done (adds to achieved_qty)
   - Progress rolls UP: parent % = target-qty-weighted average of children
   - Edits are LOCAL-first; saves happen in the background WITHOUT yanking
     the tree out from under you (no disruptive reload while editing). */

const UNITS = ['nos', 'm', 'rmt', 'sqm', 'cum', 'sft', 'cft', 'kg', 'ton', 'brass', 'bag', 'litre', 'pair', 'lot', 'lump sum', '%'];

// ---- tree helpers (immutable) ----
const kids = (n) => n.subtasks || [];
const mapTree = (nodes, id, fn) =>
    nodes.map(n => n.id === id ? fn({ ...n }) : { ...n, subtasks: mapTree(kids(n), id, fn) });
const removeFrom = (nodes, id) =>
    nodes.filter(n => n.id !== id).map(n => ({ ...n, subtasks: removeFrom(kids(n), id) }));
const addChild = (nodes, parentId, child) => {
    if (parentId == null) return [...nodes, child];
    return nodes.map(n => n.id === parentId
        ? { ...n, subtasks: [...kids(n), child] }
        : { ...n, subtasks: addChild(kids(n), parentId, child) });
};

const rollup = (node) => {
    const ch = kids(node);
    if (ch.length) {
        let tw = 0, sum = 0;
        ch.forEach(k => { const w = (Number(k.target_qty) || 0) > 0 ? Number(k.target_qty) : 1; tw += w; sum += w * rollup(k); });
        return tw ? Math.round(sum / tw) : 0;
    }
    const tgt = Number(node.target_qty) || 0, ach = Number(node.achieved_qty) || 0;
    if (tgt > 0) return Math.min(100, Math.round((ach / tgt) * 100));
    return Number(node.progress) || 0;
};

const serialize = (nodes) => (nodes || []).map(n => ({
    name: n.name || 'Unnamed Task',
    work_category: n.work_category || '',
    unit: n.unit || '',
    target_qty: Number(n.target_qty) || 0,
    achieved_qty: Number(n.achieved_qty) || 0,
    progress: Number(n.progress) || 0,
    status: n.status || 'Not Started',
    due_date: n.due_date || null,
    sort: Number(n.sort) || 0,
    subtasks: serialize(n.subtasks),
}));

const newNode = () => ({
    id: 'new-' + Date.now() + '-' + Math.floor(Math.random() * 1e6),
    name: 'New Task', work_category: '', unit: '', target_qty: 0, achieved_qty: 0,
    progress: 0, status: 'Not Started', due_date: null, sort: 0, subtasks: [],
});

const statusColor = (s, p) => (s === 'Completed' || p >= 100) ? '#0f9d58'
    : s === 'Delayed' ? '#dc2626' : s === 'Blocked' ? '#7c3aed'
    : p > 0 ? '#f59e0b' : '#94a3b8';

const inputBase = { border: '1px solid transparent', borderRadius: '6px', padding: '5px 7px', fontSize: '13px', background: 'transparent' };

export default function TasksTree({ projects, api, showToast, onReload, initialProjectId, readOnlyStructure = false, lockProject = false }) {
    const [pid, setPid] = useState(initialProjectId ?? projects[0]?.id ?? null);
    useEffect(() => { if (initialProjectId) setPid(initialProjectId); }, [initialProjectId]);
    const [tree, setTree] = useState([]);
    const [collapsed, setCollapsed] = useState({});
    const [saving, setSaving] = useState(false);
    const [workFor, setWorkFor] = useState(null);
    const [workQty, setWorkQty] = useState('');
    // Create form: { parentId } — parentId null = top-level task
    const [addFor, setAddFor] = useState(null);
    const [form, setForm] = useState({ name: '', target_qty: '', unit: '', work_category: '' });

    const project = projects.find(p => p.id === pid) || projects[0] || null;

    // We only (re)load the tree from props when the PROJECT changes — never on a
    // background save — so typing is never interrupted by a refresh.
    const loadedForRef = useRef(null);
    const dirtyRef = useRef(false);
    useEffect(() => { if (pid == null && projects.length) setPid(projects[0].id); }, [projects, pid]);
    useEffect(() => {
        if (!project) return;
        // Load fresh only when switching to a different project, or when this
        // project's tree hasn't been loaded yet AND we have no unsaved edits.
        if (loadedForRef.current !== project.id || (!dirtyRef.current && !tree.length && (project.tasks || []).length)) {
            setTree(JSON.parse(JSON.stringify(project.tasks || [])));
            loadedForRef.current = project.id;
            dirtyRef.current = false;
        }
    }, [pid, project]); // eslint-disable-line

    // Background save: persist the tree, but DO NOT reload/replace local state,
    // so the row you're editing stays exactly as-is.
    const saveTimer = useRef(null);
    const persist = (next) => {
        if (!project) return;
        dirtyRef.current = true;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            setSaving(true);
            try {
                await api(`projects/${project.id}`, 'PUT', { tasks: serialize(next) });
                dirtyRef.current = false;
            } catch { showToast?.('Could not save tasks.'); }
            finally { setSaving(false); }
        }, 600); // debounce so rapid typing = one save
    };

    const editField = (id, patch) => {
        setTree(prev => { const next = mapTree(prev, id, n => ({ ...n, ...patch })); persist(next); return next; });
    };
    // Status change: in read-only (engineer) mode use the granular endpoint so
    // it works without admin rights and without rewriting the whole tree.
    const saveStatus = async (node, status) => {
        if (readOnlyStructure) {
            setTree(prev => mapTree(prev, node.id, n => ({ ...n, status, progress: status === 'Completed' ? 100 : n.progress })));
            if (!String(node.id).startsWith('new-')) {
                try { await api('projects/task-status', 'POST', { task_id: node.id, status }); }
                catch { showToast?.('Could not update status.'); }
            }
        } else {
            editField(node.id, { status, progress: status === 'Completed' ? 100 : node.progress });
        }
    };
    const addUnder = (parentId, fields = {}) => {
        const node = { ...newNode(), ...fields };
        setTree(prev => { const next = addChild(prev, parentId, node); persist(next); return next; });
        if (parentId != null) setCollapsed(c => ({ ...c, [parentId]: false }));
    };
    // Open the create form for a new task/sub-task.
    const openAdd = (parentId) => { setAddFor({ parentId }); setForm({ name: '', target_qty: '', unit: '', work_category: '' }); };
    const submitAdd = () => {
        const name = form.name.trim();
        if (!name) { showToast?.('Enter a task name.'); return; }
        addUnder(addFor.parentId, {
            name,
            target_qty: Number(form.target_qty) || 0,
            unit: form.unit || '',
            work_category: form.work_category || '',
        });
        setAddFor(null);
    };
    const del = (id) => {
        if (!window.confirm('Delete this task and all its sub-tasks?')) return;
        setTree(prev => { const next = removeFrom(prev, id); persist(next); return next; });
    };
    const toggle = (id) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

    // Log Work Done uses the granular endpoint. New (unsaved) tasks must be
    // flushed to the server first so they have a real numeric id.
    const flushNow = async () => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        if (!project) return;
        await api(`projects/${project.id}`, 'PUT', { tasks: serialize(tree) });
        dirtyRef.current = false;
    };
    const logWork = async () => {
        const qty = parseFloat(workQty);
        if (!workFor || !qty || qty <= 0) { showToast?.('Enter a quantity.'); return; }
        setSaving(true);
        try {
            let taskId = workFor.id;
            if (String(taskId).startsWith('new-')) {
                await flushNow();
                await onReload?.();            // one intentional reload to pick up real ids
                showToast?.('Save the task, then log work again.');
                setWorkFor(null); setWorkQty(''); setSaving(false);
                return;
            }
            const res = await api('projects/task-work', 'POST', { task_id: taskId, qty });
            // Update just this node locally (no full reload).
            setTree(prev => mapTree(prev, taskId, n => ({
                ...n, achieved_qty: res?.achieved_qty ?? ((Number(n.achieved_qty) || 0) + qty),
                progress: res?.progress ?? n.progress, status: res?.status ?? n.status,
            })));
            setWorkFor(null); setWorkQty('');
            showToast?.('Work logged.');
        } catch { showToast?.('Could not log work.'); }
        finally { setSaving(false); }
    };

    let counter = 0;
    const renderRows = (nodes, depth) => nodes.flatMap(n => {
        counter += 1;
        const idx = counter;
        const ch = kids(n);
        const isLeaf = ch.length === 0;
        const pct = rollup(n);
        const isOpen = !collapsed[n.id];
        const rows = [(
            <tr key={n.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px 6px', color: '#94a3b8', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {idx}<div style={{ fontSize: '9px' }}>{String(n.id).startsWith('new-') ? 'NEW' : 'TSK' + String(n.id).padStart(5, '0').slice(-5)}</div>
                </td>
                <td style={{ padding: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', paddingLeft: `${depth * 22}px` }}>
                        {!isLeaf ? (
                            <button onClick={() => toggle(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#64748b' }}>
                                {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            </button>
                        ) : <span style={{ width: '15px', display: 'inline-block' }} />}
                        {readOnlyStructure ? (
                            <span style={{ padding: '5px 7px', fontSize: '13px', fontWeight: isLeaf ? 500 : 700 }}>{n.name}</span>
                        ) : (
                            <input value={n.name} onChange={e => editField(n.id, { name: e.target.value })}
                                style={{ ...inputBase, fontWeight: isLeaf ? 500 : 700, width: '100%', minWidth: '160px' }}
                                onFocus={e => e.target.style.border = '1px solid #cbd5e1'}
                                onBlur={e => e.target.style.border = '1px solid transparent'} />
                        )}
                    </div>
                </td>
                <td style={{ padding: '6px' }}>
                    {readOnlyStructure ? (
                        <span style={{ fontSize: '12px', color: '#64748b' }}>{n.work_category || '—'}</span>
                    ) : (
                        <input value={n.work_category || ''} placeholder="—" onChange={e => editField(n.id, { work_category: e.target.value })}
                            style={{ ...inputBase, fontSize: '12px', width: '110px' }}
                            onFocus={e => e.target.style.border = '1px solid #cbd5e1'}
                            onBlur={e => e.target.style.border = '1px solid transparent'} />
                    )}
                </td>
                <td style={{ padding: '6px' }}>
                    {isLeaf ? (
                        <select value={n.status || 'Not Started'} onChange={e => saveStatus(n, e.target.value)}
                            style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px' }}>
                            <option>Not Started</option><option>In Progress</option><option>Completed</option><option>Delayed</option><option>Blocked</option>
                        </select>
                    ) : (
                        <span style={{ fontSize: '11px', fontWeight: 700, color: statusColor(null, pct) }}>{pct >= 100 ? 'Completed' : pct > 0 ? 'In Progress' : 'Not Started'}</span>
                    )}
                </td>
                <td style={{ padding: '6px', width: '150px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <div style={{ flex: 1, height: '6px', background: '#eef2f7', borderRadius: '99px', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: statusColor(n.status, pct), borderRadius: '99px' }} />
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: 700, minWidth: '30px' }}>{pct}%</span>
                    </div>
                </td>
                <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>
                    {isLeaf ? (
                        readOnlyStructure ? (
                            <span style={{ fontSize: '12px' }}><b>{Number(n.achieved_qty) || 0}</b> / {Number(n.target_qty) || 0} {n.unit || ''}</span>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 700, minWidth: '34px', textAlign: 'right' }}>{Number(n.achieved_qty) || 0}</span>
                                <span style={{ color: '#cbd5e1' }}>/</span>
                                <input type="number" min="0" value={n.target_qty ?? 0} onChange={e => editField(n.id, { target_qty: e.target.value })}
                                    style={{ ...inputBase, border: '1px solid #e2e8f0', width: '64px', fontSize: '12px', textAlign: 'right' }} title="Target quantity" />
                                <select value={n.unit || ''} onChange={e => editField(n.id, { unit: e.target.value })}
                                    style={{ padding: '5px 4px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '11px' }} title="Unit">
                                    <option value="">unit</option>
                                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </div>
                        )
                    ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                </td>
                <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!readOnlyStructure && (
                        <button title="Add sub-task" onClick={() => openAdd(n.id)}
                            style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: '6px', padding: '4px 6px', cursor: 'pointer', marginRight: '4px' }}><Plus size={12} /></button>
                    )}
                    {isLeaf && (
                        <button title="Log work done" onClick={() => { setWorkFor(n); setWorkQty(''); }}
                            style={{ border: '1px solid #1743e6', background: '#1743e6', color: '#fff', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', marginRight: '4px', fontSize: '11px' }}>Log Work</button>
                    )}
                    {!readOnlyStructure && (
                        <button title="Delete" onClick={() => del(n.id)}
                            style={{ border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', borderRadius: '6px', padding: '4px 6px', cursor: 'pointer' }}><Trash2 size={12} /></button>
                    )}
                </td>
            </tr>
        )];
        if (!isLeaf && isOpen) rows.push(...renderRows(ch, depth + 1));
        return rows;
    });

    if (!projects.length) return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No projects yet. Create a project first.</div>;

    const overall = tree.length
        ? Math.round(tree.reduce((a, n) => a + rollup(n) * ((Number(n.target_qty) || 0) > 0 ? Number(n.target_qty) : 1), 0) /
            tree.reduce((a, n) => a + ((Number(n.target_qty) || 0) > 0 ? Number(n.target_qty) : 1), 0))
        : 0;

    return (
        <div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
                {lockProject ? (
                    <div style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: 700, minWidth: '220px', background: '#f8fafc' }}>{project?.name || '—'}</div>
                ) : (
                    <select value={pid ?? ''} onChange={e => setPid(Number(e.target.value))}
                        style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: 600, minWidth: '220px' }}>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                )}
                {!readOnlyStructure && (
                    <button onClick={() => openAdd(null)} style={{ border: '1px solid #1743e6', background: '#1743e6', color: '#fff', borderRadius: '8px', padding: '9px 14px', cursor: 'pointer', fontWeight: 700, display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        <Plus size={14} /> Add Task
                    </button>
                )}
                <span style={{ fontSize: '12px', color: saving ? '#f59e0b' : '#16a34a', fontWeight: 600 }}>{saving ? 'Saving…' : 'Saved'}</span>
                <button onClick={async () => { if (saveTimer.current) clearTimeout(saveTimer.current); await onReload?.(); loadedForRef.current = null; dirtyRef.current = false; setTree(JSON.parse(JSON.stringify((projects.find(p => p.id === pid)?.tasks) || []))); loadedForRef.current = pid; }}
                    style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Refresh</button>
                <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#64748b' }}>Overall: <b style={{ color: '#1743e6' }}>{overall}%</b></div>
            </div>

            <div style={{ background: '#fff', border: '1px solid #eef2f7', borderRadius: '12px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                            {['#', 'Name', 'Work Category', 'Status', 'Progress', 'Achieved / Target', ''].map((h, i) => (
                                <th key={i} style={{ padding: '10px 8px', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tree.length ? renderRows(tree, 0) : (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No tasks yet. Click "Add Task" to start the work breakdown.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create task / sub-task */}
            {addFor && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, backdropFilter: 'blur(3px)' }} onClick={() => setAddFor(null)}>
                    <div style={{ background: '#fff', borderRadius: '14px', width: '400px', padding: '20px', boxShadow: '0 20px 45px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <h3 style={{ margin: 0, fontSize: '15px' }}>{addFor.parentId == null ? 'Add Task' : 'Add Sub-task'}</h3>
                            <button onClick={() => setAddFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
                        </div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>Task name *</label>
                        <input autoFocus value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && submitAdd()}
                            placeholder="e.g. RCC M35" style={{ width: '100%', padding: '9px', border: '1px solid #e2e8f0', borderRadius: '8px', margin: '4px 0 12px', boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>Target quantity</label>
                                <input type="number" min="0" value={form.target_qty} onChange={e => setForm(f => ({ ...f, target_qty: e.target.value }))}
                                    placeholder="e.g. 850" style={{ width: '100%', padding: '9px', border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '4px', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ width: '110px' }}>
                                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>Unit</label>
                                <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                                    style={{ width: '100%', padding: '9px', border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '4px' }}>
                                    <option value="">unit</option>
                                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                </select>
                            </div>
                        </div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>Work category</label>
                        <input value={form.work_category} onChange={e => setForm(f => ({ ...f, work_category: e.target.value }))}
                            placeholder="e.g. Masonry, RCC, Civil" style={{ width: '100%', padding: '9px', border: '1px solid #e2e8f0', borderRadius: '8px', margin: '4px 0 16px', boxSizing: 'border-box' }} />
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '14px' }}>Tip: leave target 0 for a heading/group that will hold sub-tasks.</div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setAddFor(null)} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={submitAdd} style={{ border: 'none', background: '#1743e6', color: '#fff', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontWeight: 700 }}>Add</button>
                        </div>
                    </div>
                </div>
            )}

            {workFor && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, backdropFilter: 'blur(3px)' }} onClick={() => setWorkFor(null)}>
                    <div style={{ background: '#fff', borderRadius: '14px', width: '340px', padding: '20px', boxShadow: '0 20px 45px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '15px' }}>Log Work Done</h3>
                            <button onClick={() => setWorkFor(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
                        </div>
                        <div style={{ fontSize: '13px', color: '#334155', marginBottom: '4px' }}>{workFor.name}</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>Current: {Number(workFor.achieved_qty) || 0} / {Number(workFor.target_qty) || 0} {workFor.unit || ''}</div>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>Work done today ({workFor.unit || 'qty'})</label>
                        <input type="number" min="0" autoFocus value={workQty} onChange={e => setWorkQty(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && logWork()}
                            placeholder="Enter amount" style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '4px', marginBottom: '14px', boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setWorkFor(null)} style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={logWork} disabled={saving} style={{ border: 'none', background: '#1743e6', color: '#fff', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontWeight: 700 }}>{saving ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
